import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  TextField,
  InputAdornment,

  Skeleton,
  Tabs,
  Tab,

  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TodayIcon from '@mui/icons-material/Today';

import { alpha, keyframes } from '@mui/material/styles';
import { fetchPosts, searchPosts } from '../services/api';

// Blog categories (same as in PostCreate.js)
const BLOG_CATEGORIES = [
  'Scholarship',
  'Education',
  'Admissions'
];

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const pulse = keyframes`
  0% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
  100% { opacity: 0.4; transform: scale(1); }
`;

function HomePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Load posts on component mount
  const location = useLocation();

  // Load posts on component mount or when URL search param changes
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams(location.search);
        const search = searchParams.get('search');
        const category = searchParams.get('category');
        const tag = searchParams.get('tag');

        if (search) {
          setSearchQuery(search);
          const results = await searchPosts(search);
          setPosts(results);
        } else if (category && category !== 'all') {
          setSelectedCategory(category);
          const allPosts = await fetchPosts();
          setPosts(allPosts); // Filtering happens in render or we can fetch by category
        } else if (tag) {
          // If API supports tag search, use it. Otherwise filter client side
          const allPosts = await fetchPosts();
          setPosts(allPosts.filter(p => p.tags && p.tags.includes(tag)));
        } else {
          // Default load
          setSearchQuery('');
          const allPosts = await fetchPosts();
          setPosts(allPosts);

          // Set the most recent post as featured post
          // if (allPosts.length > 0) {
          //   setFeaturedPost(allPosts[0]);
          // }
        }
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [location.search]);

  // Handle category change
  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  // Handle search
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      const results = await searchPosts(searchQuery);
      setPosts(results);
    } catch (error) {
      console.error('Error searching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset search
  const resetSearch = async () => {
    if (searchQuery) {
      setSearchQuery('');
      try {
        setLoading(true);
        const allPosts = await fetchPosts();
        setPosts(allPosts);
      } catch (error) {
        console.error('Error resetting search:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Get filtered posts based on category
  const getFilteredPosts = () => {
    if (selectedCategory === 'all') {
      return posts;
    } else {
      return posts.filter(post => post.category === selectedCategory);
    }
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colorMap = {
      'Scholarship': '#9C27B0', // Purple
      'Education': '#357a38',   // Green
      'Admissions': '#C6FF00'   // Lime
    };

    // Default fallback
    if (!colorMap[category]) {
      return '#C6FF00'; // Default Lime
    }

    return colorMap[category] || '#757575';
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  // Get post thumbnail or placeholder
  const getPostThumbnail = (post) => {
    if (post?.thumbnailData?.data) {
      return post.thumbnailData.data;
    }
    return 'https://via.placeholder.com/800x400?text=No+Image';
  };

  const filteredPosts = getFilteredPosts();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      <Helmet>
        <title>WorldWise Scholar | International Scholarships & Study Abroad</title>
        <meta name="description" content="WorldWise Scholar - Your resource for international scholarships, university admissions and study abroad opportunities." />
        <link rel="canonical" href="https://worldwisescholar.online/" />

        {/* Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "WorldWise Scholar",
            "url": "https://worldwisescholar.online/",
            "logo": "https://worldwisescholar.online/hero_education.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+92-300-7370282",
              "contactType": "customer service"
            },
            "sameAs": [
              "https://www.facebook.com/worldwisescholar",
              "https://twitter.com/wwscholar"
            ]
          })}
        </script>

        {/* WebSite Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "WorldWise Scholar",
            "url": "https://worldwisescholar.online/",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://worldwisescholar.online/?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          pt: { xs: 8, md: 24 },
          pb: { xs: 12, md: 24 },
          overflow: 'hidden',
        }}
      >
        {/* Background Decorative Elements */}
        {/* Ambient Glows */}
        <Box sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '60%',
          height: '140%',
          background: theme.palette.mode === 'light'
            ? 'radial-gradient(circle, rgba(79, 70, 229, 0.1) 0%, rgba(255,255,255,0) 70%)'
            : 'radial-gradient(circle, rgba(129, 140, 248, 0.15) 0%, rgba(15, 23, 42, 0) 70%)',
          zIndex: 0,
          pointerEvents: 'none',
          animation: `${pulse} 8s infinite ease-in-out`,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-10%',
          width: '50%',
          height: '100%',
          background: theme.palette.mode === 'light'
            ? 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, rgba(255,255,255,0) 70%)'
            : 'radial-gradient(circle, rgba(52, 211, 153, 0.1) 0%, rgba(15, 23, 42, 0) 70%)',
          zIndex: 0,
          pointerEvents: 'none',
          animation: `${pulse} 10s infinite ease-in-out reverse`,
        }} />

        {/* Grid Pattern Overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: theme.palette.mode === 'light' ? 0.03 : 0.05,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: `linear-gradient(${theme.palette.divider} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.divider} 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} />

        {/* Floating Geometric Objects */}
        <Box sx={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: 120,
          height: 120,
          borderRadius: '24% 76% 35% 65% / 27% 36% 64% 73%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          filter: 'blur(20px)',
          zIndex: 0,
          animation: `${float} 12s infinite ease-in-out`,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          right: '5%',
          width: 180,
          height: 180,
          borderRadius: '63% 37% 54% 46% / 55% 48% 52% 45%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.light, 0.2)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          filter: 'blur(30px)',
          zIndex: 0,
          animation: `${float} 15s infinite ease-in-out reverse`,
        }} />
        <Box sx={{
          position: 'absolute',
          top: '40%',
          right: '15%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          zIndex: 0,
          animation: `${float} 9s infinite ease-in-out 1s`,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              mb: 3,
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(to right, #111827 0%, #4F46E5 100%)'
                : 'linear-gradient(to right, #F8FAFC 0%, #818CF8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2.5rem', md: '4.5rem' },
              lineHeight: 1.1,
              fontWeight: 800,
            }}
          >
            Find Your Path to <br /> Global Education
          </Typography>
          <Typography
            variant="h5"
            component="p"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              mb: 6,
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            The most comprehensive resource for international scholarships, university admissions, and study abroad opportunities.
          </Typography>

          <Box component="form" onSubmit={handleSearch} sx={{
            maxWidth: 700,
            mx: 'auto',
            position: 'relative',
          }}>
            <TextField
              fullWidth
              placeholder="Search for scholarships, universities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: '8px',
                  bgcolor: 'background.paper',
                  boxShadow: theme.palette.mode === 'light' ? '0 4px 12px -2px rgba(0,0,0,0.05)' : '0 4px 12px -2px rgba(0,0,0,0.3)',
                  height: 64,
                  fontSize: '1.1rem',
                  pl: 3,
                  '&.Mui-focused': {
                    boxShadow: theme.palette.mode === 'light' ? '0 20px 40px -10px rgba(79, 70, 229, 0.2)' : '0 20px 40px -10px rgba(0, 0, 0, 0.5)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `1px solid ${theme.palette.divider} !important`,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main + ' !important',
                  }
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main', fontSize: 28, ml: 1 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: '8px',
                      px: 4,
                      height: 48,
                      mr: 0.5,
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    Search
                  </Button>
                ),
              }}
            />
          </Box>
        </Container>

      </Box>

      <Container maxWidth="lg" sx={{ mt: 4, position: 'relative', zIndex: 3 }}>
        <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
          Latest Scholarship Articles
        </Typography>


        {/* Category Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 5,
            p: 1,
            bgcolor: 'background.paper',
            border: '1px solid rgba(0,0,0,0.05)',
            overflowX: 'auto'
          }}
        >
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant={isTablet ? "scrollable" : "standard"}
            centered={!isTablet}
            scrollButtons="auto"
            aria-label="category tabs"
            sx={{ minHeight: '48px' }}
          >
            <Tab
              label="All"
              value="all"
              sx={{ minHeight: '48px', textTransform: 'none', fontWeight: 600 }}
            />
            {BLOG_CATEGORIES.map((category) => (
              <Tab
                key={category}
                label={category}
                value={category}
                icon={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getCategoryColor(category),
                      display: 'inline-block',
                      mr: 1
                    }}
                  />
                }
                iconPosition="start"
                sx={{ minHeight: '48px', textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Search Results Info */}
        {searchQuery && (
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              Search results for "{searchQuery}"
            </Typography>
            <Button variant="outlined" onClick={resetSearch} size="small">
              Clear Search
            </Button>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Grid container spacing={4}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Posts Grid */}
        {!loading && filteredPosts.length > 0 && (
          <>
            <Grid container spacing={4} sx={{ mb: 8 }}>
              {filteredPosts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post._id || post.id}>
                  <Card
                    onClick={() => navigate(`/blog/${post._id || post.id}`)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer'
                    }}
                  >
                    <Box sx={{ position: 'relative', pt: '56.25%' /* 16:9 Aspect Ratio */ }}>
                      <CardMedia
                        component="img"
                        image={getPostThumbnail(post)}
                        alt={post.title}
                        loading="lazy"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      {post.category && (
                        <Chip
                          label={post.category}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            bgcolor: alpha(getCategoryColor(post.category), 0.9),
                            color: 'white',
                            fontWeight: 'medium',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                      )}
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {post.title}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          opacity: 0.8,
                          typography: 'body2'
                        }}
                      >

                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          <TodayIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {formatDate(post.created_at).split(',')[0]}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {post.content ? (post.content.substring(0, 100) + (post.content.length > 100 ? '...' : '')) : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {post.tags && post.tags.slice(0, 3).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                        ))}
                        {post.tags && post.tags.length > 3 && (
                          <Chip
                            label={`+${post.tags.length - 3}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* No Posts Found */}
        {!loading && filteredPosts.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              py: 6,
              px: 3,
              borderRadius: 4,
              textAlign: 'center',
              bgcolor: 'background.paper',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              mb: 8
            }}
          >
            <Typography variant="h6" component="h3" gutterBottom>
              No posts found
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {searchQuery ? `No posts matching "${searchQuery}"` : selectedCategory !== 'all' ? `No posts in the "${selectedCategory}" category` : 'There are no posts yet'}
            </Typography>
            {searchQuery && (
              <Button variant="outlined" onClick={resetSearch} sx={{ mt: 2 }}>
                Clear Search
              </Button>
            )}
            {selectedCategory !== 'all' && !searchQuery && (
              <Button variant="outlined" onClick={() => setSelectedCategory('all')} sx={{ mt: 2 }}>
                View All Posts
              </Button>
            )}
          </Paper>
        )}
      </Container>
    </Box >
  );
}

export default HomePage;